const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png']
export const IMAGE_COMPRESSION_PROFILE = 'leave-image-v1'

export const IMAGE_COMPRESSION_DEFAULTS = {
  minSize: 1 * 1024 * 1024,
  maxLongEdge: 1920,
  quality: 0.75
}

export const isCompressibleImage = (file) => {
  return Boolean(file && IMAGE_MIME_TYPES.includes(file.type))
}

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    const objectUrl = URL.createObjectURL(file)

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(image)
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Unable to load image'))
    }

    image.src = objectUrl
  })

const getTargetSize = (width, height, maxLongEdge) => {
  const longEdge = Math.max(width, height)

  if (longEdge <= maxLongEdge) {
    return { width, height }
  }

  const scale = maxLongEdge / longEdge
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale)
  }
}

const canvasToBlob = (canvas, mimeType, quality) =>
  new Promise((resolve) => {
    canvas.toBlob(resolve, mimeType, quality)
  })

export const compressImageFile = async (
  file,
  options = IMAGE_COMPRESSION_DEFAULTS
) => {
  const config = { ...IMAGE_COMPRESSION_DEFAULTS, ...options }

  if (!isCompressibleImage(file) || file.size <= config.minSize) {
    return file
  }

  try {
    const image = await loadImage(file)
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    if (!context) {
      return file
    }

    const { width, height } = getTargetSize(
      image.naturalWidth || image.width,
      image.naturalHeight || image.height,
      config.maxLongEdge
    )

    canvas.width = width
    canvas.height = height
    context.drawImage(image, 0, 0, width, height)

    const blob = await canvasToBlob(canvas, file.type, config.quality)

    if (!blob || blob.size >= file.size) {
      return file
    }

    return new File([blob], file.name, {
      type: file.type,
      lastModified: file.lastModified
    })
  } catch (error) {
    return file
  }
}

export const compressImageFileWithMeta = async (
  file,
  options = IMAGE_COMPRESSION_DEFAULTS
) => {
  const compressedFile = await compressImageFile(file, options)

  return {
    file: compressedFile,
    meta: {
      fileName: compressedFile.name,
      compressed: compressedFile !== file,
      profile: IMAGE_COMPRESSION_PROFILE
    }
  }
}
